'use client';

import { FunctionComponent, useId, useMemo, useRef } from 'react';
import {
  Breakpoint,
  Breakpoints
} from '@/react/components/core-ui/Breakpoints';

import { useLocaleMessage } from '@/react/hooks/useLocaleMessage';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import { ArrowVariant } from '@/react/components/content/registry/data/parts/sliderSettings';
import { SliderDirection } from '@/react/components/Slider/ISliderOptions';
import { useBrandLocaleValue } from '@/react/hooks/useBrandLocaleValue';
import { classes } from '@/next-utils/css-utils/scss-utils';
import { isNullOrEmpty } from '@/utils/null-utils';
import type { IProduct } from '@/services/models/Product';
import type { Nullable } from '@/type-utils';
import { EventType } from '@/services/isomorphic/UserInteractionService';
import type { IInteractionEngagementRecommendationDetails } from '@/services/isomorphic/UserInteractionService/IInteractionDetails';
import { Slide, Slider } from '../../../Slider';
import {
  IBaseRecommendationsCarouselProps,
  IBaseRecommendationsCarouselSliderProps
} from '../IBaseRecommendationsCarouselProps';
import ProductTile from '../../ProductTile';

import S from './styles.base.module.scss';
import { product_recommendations_title } from "@/lang/__generated__/ahnu/product_recommendations_title";

/**
 * Default slider props for the recommendations carousel.
 */
const defaultSliderProps = {
  slidesPerView: 1,
  mobileSlidesPerView: 1,
  direction: SliderDirection.Horizontal,
  centeredSlides: false,
  initialSlide: 0,
  navigation: {
    inset: true,
    arrowVariant: ArrowVariant.Arrow,
    buttonClassName: S.navigationButton
  },
  pagination: { enabled: false },
  height: 'auto',
  spaceBetween: 8
};

/**
 * A slider intended to show product tiles on the PDP.
 */
const DEFAULT_VARIANT = 'recommendation';

interface IInternalRecommendationsCarouselProps
  extends IBaseRecommendationsCarouselProps {
  /**
   * Slot ids corresponding to the products.
   */
  slots: Array<string>;
  /**
   * Products to display in the recommendations carousel.
   */
  products: Array<IProduct>;
  /**
   * Title of the recommendations carousel.
   */
  title: Nullable<string>;
}

/**
 * Internal recommendations carousel.
 */
export const InternalRecommendationsCarousel: FunctionComponent<
  IInternalRecommendationsCarouselProps
> = ({
  products,
  title,
  sliderProps,
  className,
  titleClassName,
  variant = 'default',
  slots
}: IInternalRecommendationsCarouselProps) => {
  const [msg] = useLocaleMessage();

  const containerRef = useRef<HTMLDivElement>(null);
  const key = useId();
  // Get the recommendations config.
  const recommendationsConfig =
    ConfigurationService.getConfig('recommendations');
  // Define the number of slides per view for desktop vs mobile, giving priority to props passed to the sliderProps prop.
  const slidesPerView =
    sliderProps?.slidesPerView ??
    recommendationsConfig.getSetting('pdp.slider.slidesPerView').value;
  const mobileSlidesPerView =
    sliderProps?.mobileSlidesPerView ??
    recommendationsConfig.getSetting('pdp.slider.mobileSlidesPerView').value;
  /* Navigation props checks to see  if navigation enables is defined in the sliderProps. If not, it gets the value from the recommendations config. */
  const navigationProps: boolean | undefined =
    typeof sliderProps?.navigation !== 'undefined'
      ? typeof sliderProps.navigation !== 'boolean'
        ? sliderProps.navigation.enabled
        : sliderProps.navigation
      : undefined;
  const navigationEnabled: boolean =
    typeof navigationProps === 'undefined'
      ? recommendationsConfig.getSetting('pdp.slider.navigation.enabled').value
      : navigationProps;

  const paginationProps: boolean | undefined =
    typeof sliderProps?.pagination !== 'undefined'
      ? typeof sliderProps.pagination !== 'boolean'
        ? sliderProps.pagination.enabled
        : sliderProps.pagination
      : undefined;
  const paginationEnabled: boolean =
    typeof paginationProps === 'undefined'
      ? recommendationsConfig.getSetting('pdp.slider.pagination.enabled').value
      : paginationProps;
  const loop =
    slidesPerView === 'auto' ? true : !(slidesPerView > products.length);

  /**
   * Allow to set default slider settings per brand.
   */
  const defaultSettings =
    useBrandLocaleValue<IBaseRecommendationsCarouselSliderProps>(
      () => ({
        default: { ...defaultSliderProps }
      }),
      []
    );

  const sliderSettings = {
    ...defaultSettings,
    ...(sliderProps && { ...sliderProps }),
    slidesPerView
  };

  /**
   * Generates the slides for the slider.
   * @returns Slides - array of slides to be rendered in the slider.
   */
  const slides = useMemo(() => {
    if (products.length) {
      return products?.map((product, index) => {
        const key = `${product.sku}_${index}`;

        return (
          <Slide key={key}>
            <div className={S.slide}>
              <ProductTile
                product={product}
                variant={DEFAULT_VARIANT}
                showRatingStars={false}
                interactionDetails={
                  {
                    action: EventType.EngagementRecommendationClick,
                    slotId: slots[index]
                  } as IInteractionEngagementRecommendationDetails
                }
              />
            </div>
          </Slide>
        );
      });
    }
    return null;
  }, [products, slots]);

  return (
    <div ref={containerRef} className={classes(S.container, className)}>
      {variant !== 'tab' && !isNullOrEmpty(title) && (
        <div className={classes(S.title, titleClassName)}>
          {title ?? msg(product_recommendations_title)}
        </div>
      )}
      <Breakpoints>
        <Breakpoint media="md:xl">
          <Slider
            key={`${key}_slider`}
            {...sliderSettings}
            sliderClassName={S.slider}
            navigationWrapperClassName={S.wrapper}
          >
            {slides}
          </Slider>
        </Breakpoint>
        <Breakpoint default>
          <Slider
            key={`${key}_slider_mobile`}
            {...sliderSettings}
            slidesPerView={mobileSlidesPerView ?? sliderSettings.slidesPerView}
            sliderClassName={S.slider}
            navigationWrapperClassName={S.wrapper}
          >
            {slides}
          </Slider>
        </Breakpoint>
      </Breakpoints>
    </div>
  );
};
